// composables/useAuth.js - Authentication composable with SSOT pattern
import { useAsyncState } from '@vueuse/core';
import { useRepo } from 'pinia-orm';
import { computed, readonly } from 'vue';
import { isReplayOn } from '@/collector/previewReplay.js';
import { apiClient, authClient, jsonApiClient } from '@/httpConfig/config/axios.js';
import OrderBox from '@/models/commerce/OrderBox.js';
import { isTelegramEnv } from '@/platform/index.js';
import { AUTH_PERSIST_KEY, useAuthStore } from '@/stores/authStore';

/**
 * Lazy-imported push hooks. Imported on demand so that:
 *   - TMA (gated below) never pulls the OneSignal dep into the boot bundle.
 *   - PWA dev/preview without the SDK installed degrades to a console warn
 *     (not a hard crash) so existing auth flows are unaffected.
 * The composable's own module-state guards ensure ensurePushIdentity /
 * pushLogoutCleanup are no-ops when init() hasn't run yet.
 */
async function pushBridge() {
  if (isTelegramEnv()) return null;
  try {
    const mod = await import('@/composables/usePushNotifications.js');
    return {
      ensurePushIdentity: mod.ensurePushIdentity,
      pushLogoutCleanup: mod.pushLogoutCleanup,
    };
  } catch (err) {
    console.warn('[useAuth] push bridge load failed:', err);
    return null;
  }
}

/**
 * SINGLE identity chokepoint (plan Section 7.3). Establishes the authenticated
 * session AND syncs the OneSignal push identity so external_id can never
 * drift from authStore.user.id. Every authenticated-user landing (email +
 * Telegram login, session refresh, profile update) routes through here.
 *
 * Push sync is fire-and-forget — never blocks auth, never throws, PWA-only
 * (pushBridge TMA-gates), and idempotent (ensurePushIdentity short-circuits
 * on a matching externalId and no-ops until OneSignal.init() resolves) — so
 * it is safe to call on same-user refreshes too.
 *
 * @param {Object} userData - deserialized profile user object
 * @param {Object|null} [delegation] - the `meta.delegation` block (act-as
 *   banner signal); threaded into setSession so the store's delegation state
 *   tracks the profile fetch. See fetchUserProfile.
 */
function setSessionAndSyncPush(userData, delegation = null) {
  useAuthStore().setSession(userData, delegation);

  // Bring the realtime socket up for THIS identity. initRealtime() is idempotent
  // (guarded by an internal `started` flag) so this is a no-op on same-user
  // refreshes/profile-updates; it only does real work on the first authenticated
  // landing of a page session — notably an in-place re-login after logout (which
  // called teardownRealtime) or a fresh runtime login, where App.vue's boot-time
  // init won't re-run. NOT TMA-gated — Poolit channels are private Sanctum
  // channels that work in both hosts, matching App.vue's boot-time init.
  //
  // Lazy-imported (like pushBridge below) so realtime/echo.js — and its
  // realtimeStore.js `defineStore` side-effect — stays OUT of the static import
  // graph of this central composable. A static import here pulled realtimeStore
  // into every test that mocks pinia partially and transitively imports useAuth.
  // Fire-and-forget: a realtime failure must never break session establishment.
  // Not under an offline supplier preview (supplier-preview-offline L-J).
  if (!isReplayOn()) {
    import('@/realtime/echo.js')
      .then((mod) => mod.initRealtime())
      .catch((err) => console.warn('[useAuth] initRealtime failed:', err));
  }

  pushBridge()
    .then((bridge) => bridge?.ensurePushIdentity?.())
    .catch((err) => console.warn('[useAuth] ensurePushIdentity failed:', err));
}

// ─────────────────────────────────────────────────────────────────────────────
// BACKEND-TODO — required companion change for persistent login
// ─────────────────────────────────────────────────────────────────────────────
// The frontend now caches the user/organization/membership via
// pinia-plugin-persistedstate (see authStore.js `persist` block) so the
// authenticated UI mounts instantly on cold boot. The cached UI is only
// useful while the Sanctum session cookie is still valid on the backend.
//
// In the Laravel backend repo (.env), set:
//   SESSION_LIFETIME=43200            # 30 days, in minutes
//   SESSION_EXPIRE_ON_CLOSE=false     # cookie survives browser/PWA close
//   SESSION_DRIVER=database           # if not already (file driver dies on
//                                     #  container restart)
//
// Without the env change, the cached UI flashes for ~500 ms on every cold
// boot and the user is bounced to /login because the cookie expired.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * SINGLETON PATTERN: This composable provides a clean API to the shared auth store.
 * All components calling useAuth() see the same reactive state.
 *
 * SSOT PRINCIPLE: The auth store is the ONLY source of user data.
 * This composable only orchestrates API calls and updates the store.
 *
 * SIMPLIFIED: No separate profile store, no transformation layers, no service files.
 * Everything goes through this composable -> auth store.
 */

/**
 * Fetch current user with all relationships.
 *
 * Returns `{ user, delegation }`:
 *   - `user` — the deserialized profile object (organization + membership
 *     nested), or null.
 *   - `delegation` — the act-as "acting as" banner signal, or null when not
 *     impersonating / not an operator.
 *
 * Where `delegation` lives (non-obvious): the backend emits it as RESOURCE-level
 * meta (`data.meta.delegation`). jsona's deserializer attaches resource meta to
 * the model as `.meta`, so it arrives on the deserialized user at
 * `user.meta.delegation`. It is NOT on `response.data.meta` — after
 * transformJsonApiResponse that is the DOCUMENT-level meta (empty here). Read
 * the resource path, then fall back to the document meta defensively in case the
 * jsona transform is ever bypassed.
 */
/**
 * The profile read's `include` — shared with the offline supplier preview's
 * recorder (collector/previewReplay.js), which must save exactly this read.
 */
export const PROFILE_INCLUDE = 'telegramUser,primaryOrganization,activeMembership,activeMembership.orderBoxes';

export async function fetchUserProfile() {
  try {
    const response = await jsonApiClient.get('/users/-actions/profile', {
      params: {
        include: PROFILE_INCLUDE,
      },
    });

    console.log('[useAuth] User profile fetched', response.data?.data);
    if (response.data?.data?.activeMembership?.orderBoxes){
      try {
        useRepo(OrderBox).save(response.data?.data?.activeMembership.orderBoxes);
      } catch (err) {
        // pinia-orm save can fail if the schema drifted between deploys;
        // the profile fetch itself succeeded, so we don't want to escalate.
        console.warn('[useAuth] OrderBox save failed:', err);
      }
    }

    const user = response.data?.data || null;
    const delegation = user?.meta?.delegation ?? response.data?.meta?.delegation ?? null;

    return { user, delegation };
  } catch (error) {
    console.error('[useAuth] Fetch profile error:', error.response?.status);
    throw {
      message: error.response?.data?.message || 'Failed to fetch user profile',
      status: error.response?.status,
    };
  }
}

/**
 * Update user profile (organization data)
 */
async function updateUserProfile(profileData) {
  const authStore = useAuthStore();
  try {
    // Format phone if provided
    if (profileData.phone && !profileData.phone.startsWith('+20')) {
      profileData.phone = `+20${profileData.phone.replace(/^0+/, '')}`;
    }

    const response = await jsonApiClient.patch(`/users/${authStore.user.id}/-actions/profile`, {
      data: {
        type: 'users',
        attributes: {
          name: profileData.name,
          organization: {
            business_name: profileData.business_name || null,
            phone: profileData.phone,
            address: profileData.address?.trim() || null,
            governorate: profileData.governorate,
          },
        },
      },
    }, {
      params: {
        include: 'telegramUser,primaryOrganization,activeMembership',
      },
    });

    console.log('[useAuth] Profile updated');
    return response.data?.data || null;
  } catch (error) {
    console.error('[useAuth] Update profile error:', error.response?.status);
    throw {
      message: error.response?.data?.message || 'Failed to update profile',
      status: error.response?.status,
    };
  }
}

/**
 * Login with Telegram init_data
 */
async function loginWithTelegram(initData) {
  const authStore = useAuthStore();

  if (!initData) {
    authStore.setError('Telegram init_data is required');
    return false;
  }

  console.log('[useAuth] Telegram login starting...');
  authStore.setLoading(true);
  authStore.clearError();

  try {
    await apiClient.post('/auth/telegram', { init_data: initData });

    // Fetch complete user profile after successful auth
    const { user, delegation } = await fetchUserProfile();

    if (user) {
      // Single identity chokepoint (plan Section 7.3) — session + push identity sync.
      setSessionAndSyncPush(user, delegation);
      return true;
    }

    throw new Error('Failed to load user profile after login');
  } catch (error) {
    const errorMsg = error.message || 'Telegram login failed';
    console.error('[useAuth] Telegram login error:', errorMsg);
    authStore.setError(errorMsg);
    return false;
  } finally {
    authStore.setLoading(false);
  }
}

/**
 * Login with email/password
 */
async function login(credentials) {
  const authStore = useAuthStore();

  if (!credentials?.email || !credentials?.password) {
    authStore.setError('Email and password are required');
    return false;
  }

  console.log('[useAuth] Email login starting...');
  authStore.setLoading(true);
  authStore.clearError();

  try {
    await authClient.post('/login', credentials);

    // Fetch complete user profile after successful auth
    const { user, delegation } = await fetchUserProfile();

    if (user) {
      // Single identity chokepoint (plan Section 7.3) — email login was missing the
      // push identity sync the Telegram path already had (gap G2).
      setSessionAndSyncPush(user, delegation);
      return true;
    }

    throw new Error('Failed to load user profile after login');
  } catch (error) {
    const errorMsg = error.message || 'Login failed';
    console.error('[useAuth] Login error:', errorMsg);
    authStore.setError(errorMsg);
    return false;
  } finally {
    authStore.setLoading(false);
  }
}

/**
 * Logout current user
 *
 * Three-step teardown (in order):
 *   1. Tell the backend to invalidate the session cookie.
 *   2. Clear the in-memory store. pinia-plugin-persistedstate watches the
 *      store and writes the resulting nulls to localStorage automatically,
 *      but step 3 is explicit insurance against any race where the store
 *      mutation hasn't been observed yet.
 *   3. Wipe the persisted localStorage entry directly. This guarantees a
 *      clean cold-boot state even if the in-memory clear was skipped (e.g.,
 *      if a future caller invokes logout() outside the normal Pinia scope).
 */
async function logout() {
  const authStore = useAuthStore();

  console.log('[useAuth] Logout starting...');
  authStore.setLoading(true);

  try {
    await authClient.post('/logout');
  } catch (error) {
    console.error('[useAuth] Logout API error:', error);
  } finally {
    // Detach this device's push subscription from the user BEFORE clearing
    // local session — without this the next user on a shared device inherits
    // the prior user's OneSignal external_id and receives their money/order
    // pushes (gap G1; plan invariant "optOut() on every logout"). Awaited so
    // the detach completes before logout returns, but defensively wrapped so a
    // slow/failed SDK never strands logout. PWA-only via pushBridge's TMA gate;
    // no-op until OneSignal.init() has resolved.
    try {
      const bridge = await pushBridge();
      await bridge?.pushLogoutCleanup?.();
    } catch (err) {
      console.warn('[useAuth] pushLogoutCleanup failed:', err);
    }
    // Disconnect the websocket + drop every subscription BEFORE clearing the
    // session, so no private channel lingers authenticated as the departing
    // user (the next user on a shared device must never inherit the prior
    // user's realtime feed). Lazy-imported (see setSessionAndSyncPush) to keep
    // realtime/echo.js out of this composable's static graph. Awaited so the
    // teardown completes before the session is wiped; best-effort — a teardown
    // failure must never strand logout. Re-init happens on the next login via
    // setSessionAndSyncPush.
    try {
      const { teardownRealtime } = await import('@/realtime/echo.js');
      teardownRealtime();
    } catch (err) {
      console.warn('[useAuth] teardownRealtime failed:', err);
    }
    // A collector's saved shop previews go with an explicit logout (never a
    // 401) — supplier-preview-offline L-G. Lazy and best-effort.
    if (authStore.isCollector) {
      import('@/collector/previewStore.js')
        .then(({ previewStore }) => previewStore().clear())
        .catch((err) => console.warn('[useAuth] clearing the preview copies failed:', err));
    }
    authStore.clearSession();
    try {
      localStorage.removeItem(AUTH_PERSIST_KEY);
    } catch {
      /* storage may be blocked (private mode, quota); store clear is enough */
    }
    authStore.setLoading(false);
    console.log('[useAuth] Logout complete');
  }

  return true;
}

/**
 * Refresh user session (re-fetch from API)
 *
 * @param {Object} [options]
 * @param {boolean} [options.silent=false] - When true, skip the global loading
 *   spinner so background/focus-triggered refreshes don't flash the UI.
 *   Errors and session-clear behaviour are unchanged.
 */
async function refreshSession(options = {}) {
  const authStore = useAuthStore();

  if (!authStore.isAuthenticated) {
    console.warn('[useAuth] Cannot refresh - not authenticated');
    return false;
  }

  console.log('[useAuth] Refreshing session...');
  const showLoading = !options.silent;
  if (showLoading) {
    authStore.setLoading(true);
  }

  try {
    const { user, delegation } = await fetchUserProfile();

    if (user) {
      setSessionAndSyncPush(user, delegation);
      return true;
    }

    return false;
  } catch (error) {
    console.error('[useAuth] Session refresh failed:', error.message);
    authStore.setError(error.message);

    // Clear auth on 401 (expired session)
    if (error.status === 401) {
      console.log('[useAuth] Session expired, clearing auth');
      authStore.clearSession();
    }

    return false;
  } finally {
    if (showLoading) {
      authStore.setLoading(false);
    }
  }
}

/**
 * Update user profile data
 */
async function updateProfile(profileData) {
  const authStore = useAuthStore();

  if (!authStore.isAuthenticated) {
    authStore.setError('Authentication required');
    return false;
  }

  console.log('[useAuth] Updating profile...');
  authStore.setLoading(true);
  authStore.clearError();

  try {
    // Validate required fields
    const errors = [];
    if (!profileData.name?.trim()) errors.push('Name is required');
    if (!profileData.phone?.trim()) errors.push('Phone is required');
    if (!profileData.governorate?.trim()) errors.push('Governorate is required');

    if (errors.length > 0) {
      throw new Error(errors[0]);
    }

    const userData = await updateUserProfile(profileData);

    if (userData) {
      // The PATCH profile response carries the same resource-meta delegation
      // block as fetchUserProfile (path contains "profile"); surface it so the
      // banner state stays consistent after a profile edit.
      setSessionAndSyncPush(userData, userData?.meta?.delegation ?? null);
      return true;
    }

    return false;
  } catch (error) {
    const errorMsg = error.message || 'Failed to update profile';
    console.error('[useAuth] Update profile error:', errorMsg);
    authStore.setError(errorMsg);
    return false;
  } finally {
    authStore.setLoading(false);
  }
}

/**
 * Initialize authentication on app startup
 *
 * WORKFLOW:
 * 1. Check for Telegram user change (different user)
 * 2. Validate existing persisted session (cached user)
 * 3. Cookie-bootstrap — fetch profile when a candidate Sanctum session
 *    cookie is in the jar but localStorage is empty (e.g. first OIDC
 *    callback return on web, returning visit after cleared site data).
 * 4. Attempt Telegram auto-login (no session)
 *
 * Profile is ALWAYS fetched as part of authentication, never separately.
 */
export function useAuthInitialize() {
  const authStore = useAuthStore();

  function initialize(options = {}) {
    console.log('[useAuth] Starting initialization...');

    return useAsyncState(
      async () => {
        authStore.setLoading(true);

        try {
          // Priority 1: Telegram User Change Detection
          if (options.telegramInitData) {
            const { initData } = await import('@telegram-apps/sdk-vue');
            const currentTelegramId = initData.user().id;
            const storedTelegramId = authStore.telegramUserId;

            if (currentTelegramId !== storedTelegramId) {
              console.log('[useAuth] Different Telegram user, re-authenticating');
              await logout();

              const success = await loginWithTelegram(options.telegramInitData);
              return { authenticated: success, method: 'telegram-new' };
            }
          }

          // Priority 2: Existing persisted session — render immediately,
          // refresh in the background.
          //
          // Don't `await` the profile fetch: that blocks `isReady` in App.vue
          // for the network round-trip and produces a "blank LoadingLayout
          // for 500 ms" flash on every cold start. With the auth store
          // persisted to localStorage, isAuthenticated is already true here
          // because pinia-plugin-persistedstate hydrated it synchronously.
          //
          // Failure modes:
          //   - 200 → setSession() with fresh data; UI updates seamlessly.
          //   - 401 (cookie expired) → clear session; the auth:unauthorized
          //     interceptor in App.vue handles the redirect to /login.
          //   - Network error → silent; user sees cached UI until next
          //     authed action also fails (which then hits the interceptor).
          if (authStore.isAuthenticated) {
            console.log('[useAuth] Existing session found, refreshing in background');

            fetchUserProfile()
              .then(({ user, delegation }) => {
                if (user) {
                  authStore.setSession(user, delegation);
                  console.log('[useAuth] Background refresh complete');
                }
                return user;
              })
              .catch((error) => {
                if (error.status === 401) {
                  console.log('[useAuth] Session expired, clearing');
                  authStore.clearSession();
                } else {
                  console.error('[useAuth] Background refresh error:', error.message);
                }
                return null;
              });

            return { authenticated: true, method: 'cached-instant' };
          }

          // Priority 3: Cookie-bootstrap recovery.
          //
          // Sanctum session cookie may be present without populating
          // localStorage — typical case is the first Telegram OIDC return
          // on a device. The HttpOnly poolit_session cookie isn't visible
          // to JS, but the companion XSRF-TOKEN cookie is, and is set by
          // the same backend response. Use it as a cheap heuristic before
          // spending a network round-trip.
          //
          //   200 → setSession() then return authenticated.
          //   401 → falls through. The shared 401 interceptor in
          //         api-interceptors.js already emits 'auth:unauthorized',
          //         which App.vue's bus listener handles by clearing the
          //         store + navigating to /login/. No duplicate handling.
          //   other → log, fall through to remaining priorities.
          //
          // Skipping when the cookie is absent saves a request on
          // first-ever visits with no session at all.
          if (typeof document !== 'undefined' && document.cookie.includes('XSRF-TOKEN=')) {
            console.log('[useAuth] Candidate session cookie detected, attempting profile fetch');
            try {
              const { user, delegation } = await fetchUserProfile();
              if (user) {
                authStore.setSession(user, delegation);
                return { authenticated: true, method: 'cookie-bootstrap' };
              }
            } catch (error) {
              if (error.status !== 401) {
                console.warn('[useAuth] Cookie bootstrap non-401 error:', error.message);
              }
            }
          }

          // Priority 4: Telegram Auto-login (no valid session)
          if (options.telegramInitData) {
            console.log('[useAuth] No valid session, attempting Telegram auto-login');
            const success = await loginWithTelegram(options.telegramInitData);
            return { authenticated: success, method: 'telegram-auto' };
          }

          // No authentication possible
          console.log('[useAuth] No valid session or auth method available');
          return { authenticated: false, method: 'none' };

        } catch (error) {
          console.error('[useAuth] Initialize error:', error);
          authStore.setError(error.message || 'Initialization failed');
          throw error;
        } finally {
          authStore.setLoading(false);
        }
      },
      { authenticated: false, method: 'pending' },
      {
        immediate: true,
        resetOnExecute: false,
        onError: (error) => {
          console.error('[useAuth] Async init error:', error);
          authStore.setError(error.message || 'Authentication failed');
        },
      },
    );
  }

  return { initialize };
}

/**
 * Runtime auth composable for components
 *
 * SINGLETON PATTERN: Returns readonly computed properties that reference
 * the shared Pinia store. All components see the same reactive state.
 *
 * READONLY: Prevents direct mutation from components. All updates must go
 * through the provided methods (login, logout, updateProfile, etc).
 */
export function useAuth() {
  const authStore = useAuthStore();

  return {
    // ===== READONLY REACTIVE STATE =====
    // Automatically update across all components
    isAuthenticated: computed(() => authStore.isAuthenticated),
    isLoading: computed(() => authStore.isLoading),
    error: computed(() => authStore.error),

    // User data
    user: readonly(computed(() => authStore.user)),
    displayName: computed(() => authStore.displayName),

    // Organization data
    organization: readonly(computed(() => authStore.organization)),
    hasOrganization: computed(() => authStore.hasOrganization),

    // Membership data
    membership: readonly(computed(() => authStore.membership)),
    hasActiveMembership: computed(() => authStore.hasActiveMembership),
    needsRegistration: computed(() => authStore.needsRegistration),

    // Notification reliability signals (contract Section 8). Used by the
    // "link Telegram for reliable alerts" CTA on ProfilePage and the
    // Huawei battery coach gate.
    telegramLinked: computed(() => authStore.telegramLinked),
    pushReachable: computed(() => authStore.pushReachable),

    // Supplier-Wall gate (P7-A) — reveals the ProfilePage supplier-mode entry.
    isSupplier: computed(() => authStore.isSupplier),
    supplierOrgId: computed(() => authStore.supplierOrgId),

    // Collector gate (collector-app P1-C) — arms requireCollector and /collector/.
    isCollector: computed(() => authStore.isCollector),

    // A collector's live supplier preview (supplier-preview P2-A): lets the supplier
    // tabbar show for a user with no club membership.
    isSupplierPreview: computed(() => authStore.isSupplierPreview),

    // ===== METHODS =====
    // Update the shared singleton store
    login,
    loginWithTelegram,
    logout,
    refreshSession,
    updateProfile,
  };
}
